/**
 * Money, in minor units.
 *
 * The API sends every amount as an integer count of the currency's smallest
 * unit: cents for USD, kobo for NGN. Nothing here ever converts to a float and
 * back, because that is how a fare of 250000 kobo becomes 2499.9999999999995.
 *
 * The one place a decimal appears is the moment before it is shown to a person,
 * and `Intl.NumberFormat` does that conversion itself given the minor units, so
 * this module's job is to hand it the right divisor and never to do the
 * arithmetic itself.
 */

/**
 * How many minor units make one major unit, by ISO 4217 code.
 *
 * Only the exceptions are listed. Everything absent is the two-decimal default,
 * which covers NGN, USD, GBP and EUR. Zero-decimal currencies matter because
 * dividing a JPY amount by 100 shows a hundredth of the real price.
 */
const MINOR_UNITS: Record<string, number> = {
    BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
    PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
    BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
};

/**
 * The currency to assume when the server has not said.
 *
 * It must agree with the database, which defaults every money column to 'USD'
 * (migrations 00004_rides.sql and 00013_wallet.sql). Six screens each declared
 * "NGN" locally instead, so the web showed a naira price for a row the API had
 * stored as dollars: not a formatting difference, a different number of the
 * wrong unit, and the label on the request form read "What would you pay?
 * (NGN)" while the amount was posted and stored as USD.
 *
 * A fallback that disagrees with the server's own default is the whole bug, so
 * there is one of these and a test that no screen reintroduces a local copy.
 */
export const DEFAULT_CURRENCY = "USD";

/**
 * The locale used to render an amount.
 *
 * Not cosmetic. `Intl.NumberFormat` shows the currency the way the LOCALE
 * writes it, so USD under "en-NG" renders "US$2,500.00" and under "en-US"
 * renders "$2,500.00". The old default was "en-NG", which put a country prefix
 * on every dollar amount in the application.
 */
export const DEFAULT_LOCALE = "en-US";

function decimalsFor(currency: string): number {
    return MINOR_UNITS[currency.toUpperCase()] ?? 2;
}

/**
 * Formats an amount for display, e.g. `formatMoney(250000, "USD")` is "$2,500.00".
 *
 * Falls back to the plain code and number if the runtime does not know the
 * currency, which is better than throwing inside a render.
 */
export function formatMoney(
    minor: number | null | undefined,
    currency: string,
    locale = DEFAULT_LOCALE
): string {
    if (minor === null || minor === undefined) return "";
    const decimals = decimalsFor(currency);
    const major = minor / 10 ** decimals;

    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: currency.toUpperCase(),
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(major);
    } catch {
        return `${currency.toUpperCase()} ${major.toFixed(decimals)}`;
    }
}

/**
 * Turns what someone typed into minor units.
 *
 * Rounds rather than truncating, so "24.999" becomes 2500 kobo rather than
 * 2499. Returns null for anything that is not a number, which is what a form
 * should treat as "not filled in yet" rather than as zero.
 */
export function toMinor(
    input: string | number,
    currency: string
): number | null {
    if (typeof input === "number") {
        if (!Number.isFinite(input)) return null;
        return Math.round(input * 10 ** decimalsFor(currency));
    }

    // `Number("")` and `Number("   ")` are both 0, so an empty field would
    // otherwise be read as a fare of zero rather than as one nobody filled in.
    const text = input.replace(/,/g, "").trim();
    if (text === "") return null;

    const value = Number(text);
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 10 ** decimalsFor(currency));
}

/**
 * The symbol a money input should wear, e.g. "$" for USD under en-US.
 *
 * Derived from the same `Intl` call `formatMoney` uses rather than from a table,
 * so the character in front of an empty field is by construction the one that
 * will be in front of the amount once it is saved and read back. A hand-kept
 * map is how a field invites "$" and the receipt says "US$".
 *
 * Falls back to the ISO code, which is never wrong, only wider.
 */
export function currencySymbol(
    currency: string,
    locale = DEFAULT_LOCALE
): string {
    const code = currency.toUpperCase();
    try {
        const parts = new Intl.NumberFormat(locale, {
            style: "currency",
            currency: code,
            minimumFractionDigits: decimalsFor(code),
        }).formatToParts(0);
        return parts.find((part) => part.type === "currency")?.value ?? code;
    } catch {
        return code;
    }
}

/**
 * The `step` a numeric money input should take, as the attribute wants it.
 *
 * A hardcoded "0.01" is wrong for the zero-decimal currencies: a JPY field
 * would accept ¥100.25, which is not an amount that exists, and the browser
 * would call it valid. Three decimals matter for the dinars for the same reason
 * in the other direction, where "0.01" refuses a legitimate fils.
 */
export function stepFor(currency: string): string {
    const decimals = decimalsFor(currency);
    return decimals === 0 ? "1" : `0.${"0".repeat(decimals - 1)}1`;
}

/** The major-unit value, for pre-filling a numeric input. */
export function toMajor(minor: number, currency: string): number {
    return minor / 10 ** decimalsFor(currency);
}

/**
 * How far one press of a money field's stepper moves the amount, in minor
 * units.
 *
 * Half a major unit where the currency has one, which is fifty cents for USD
 * and the same shape for every other two-decimal currency. Zero-decimal
 * currencies have no half: ¥0.5 is not an amount and ¥50 is not a coin, while
 * ¥100 is both, so those step by a hundred of their only unit.
 */
export function bumpFor(currency: string): number {
    const decimals = decimalsFor(currency);
    return decimals === 0 ? 100 : 10 ** decimals / 2;
}

/**
 * An amount in minor units, written the way a money field shows it: digits and
 * a separator, no symbol and no grouping, so the result can go straight back
 * into an input's `value`.
 *
 * Integer division and remainder rather than `minor / 100`, for the reason at
 * the top of this file. `formatMoney` can hand the division to `Intl` because
 * `Intl` is told the number of decimals; here there is nobody to hand it to.
 */
export function toAmountText(
    minor: number | null | undefined,
    currency: string
): string {
    if (minor === null || minor === undefined || !Number.isFinite(minor)) {
        return "";
    }

    const decimals = decimalsFor(currency);
    const rounded = Math.round(minor);
    const sign = rounded < 0 ? "-" : "";
    const absolute = Math.abs(rounded);
    if (decimals === 0) return `${sign}${absolute}`;

    const scale = 10 ** decimals;
    const whole = Math.floor(absolute / scale);
    const part = String(absolute % scale).padStart(decimals, "0");
    return `${sign}${whole}.${part}`;
}

/**
 * The text a money field should hold after one press of its stepper.
 *
 * Empty stays empty on the way down. An optional fare field that is blank means
 * "no offer" and one holding 0.00 means "I will pay nothing"; they are posted
 * as null and as 0, they are different requests, and a press of the minus
 * button on an untouched field must not turn the first into the second.
 *
 * Plain addition, not snapping to the nearest multiple of the bump. The button
 * says it adds fifty cents and it adds fifty cents: a native number input's
 * `stepUp()` snaps to the step base instead, so 12.35 becomes 12.50 and the
 * press is worth fifteen.
 */
export function bumpAmount(
    text: string,
    currency: string,
    direction: 1 | -1
): string {
    const current = toMinor(text, currency);
    if (current === null && direction < 0) return "";

    const next = Math.max(0, (current ?? 0) + direction * bumpFor(currency));
    return toAmountText(next, currency);
}

/**
 * What a money field should hold once someone has finished typing in it.
 *
 * "12.5", "12." and "0012.50" are all things a person types and none of them
 * are how money is written. Anything that is not an amount at all becomes
 * empty, which is the state the field started in and the state that posts as
 * "they did not say".
 */
export function normaliseAmountText(text: string, currency: string): string {
    const minor = toMinor(text, currency);
    return minor === null ? "" : toAmountText(minor, currency);
}

/**
 * What a money field should accept while someone is still typing.
 *
 * Filtering the keystroke rather than validating the submit, so the field can
 * never hold "12.3.4" or "1e5" in the first place. `type="number"` does some of
 * this in the browser, but it hands back an empty string for a value it
 * considers invalid, which drops characters mid-entry, and it draws its own
 * spinners beside the ones a stepper already has.
 *
 * A trailing separator survives: "12." is somebody halfway through typing
 * "12.50", and removing it moves their caret out from under them.
 *
 * A comma is a group separator, not a decimal point, because that is what
 * `toMinor` has always taken it for and what `formatMoney` writes under this
 * app's locale. The two have to agree: reading it the other way turned a pasted
 * "$1,250.00" into "1.25", which is a fare wrong by three orders of magnitude
 * in a field that looked like it had accepted the paste.
 */
export function acceptAmountText(typed: string, currency: string): string {
    const decimals = decimalsFor(currency);
    const cleaned = typed.replace(/[^\d.]/g, "");
    if (decimals === 0) return cleaned.replace(/\./g, "");

    const [whole, ...rest] = cleaned.split(".");
    if (rest.length === 0) return whole;
    return `${whole}.${rest.join("").slice(0, decimals)}`;
}
